import moment from 'moment';
import type {
    ApiTransportOrderAddress,
    ApiTransportOrderConfirmation,
    ApiTransportOrderForShipment,
    ApiTransportOrderHandlingUnit,
    ApiTransportOrderHandlingUnitContent,
    ApiTransportOrdersForShipmentsResponse,
    ApiTransportOrderShippingParty,
    ApiTransportOrderShippingPartyWithAddress,
    ApiTransportOrderTimeWindow,
} from './transportExecution.transportOrder.schema';
import type {
    Address,
    ShippingParty as TransportShippingParty,
    TimeWindow,
    TransportHandlingUnit,
    TransportHandlingUnitContent,
    TransportOrder,
    TransportOrderConfirmation,
    TransportOrderShippingPartyWithAddress,
} from '../../domain/model/transport/Transport.types';

const toTransportOrderShippingPartyWithAddress = (
    src: ApiTransportOrderShippingPartyWithAddress
): TransportOrderShippingPartyWithAddress => ({
    duns: src.duns,
    name: src.name ?? undefined,
    supplierCode: src.supplier_code ?? undefined,
    handlingLocationId: src.handling_location_id ?? undefined,
    plantCode: src.plant_code ?? undefined,
    address: src.address ? toTransportOrderAddress(src.address) : undefined,
});

const toTransportOrderShippingParty = (src: ApiTransportOrderShippingParty): TransportShippingParty => ({
    duns: src.duns,
    supplierCode: src.supplier_code ?? undefined,
    name: src.name ?? undefined,
});

const toTransportOrderAddress = (src: ApiTransportOrderAddress): Address => ({
    street: src.street,
    city: src.city,
    postalCode: src.postal_code,
    countryCode: src.country_code,
});

const toTransportOrderTimeWindow = (src: ApiTransportOrderTimeWindow): TimeWindow => ({
    from: src.from,
    to: src.to,
});

const toTransportOrderHandlingUnitContent = (
    src: ApiTransportOrderHandlingUnitContent
): TransportHandlingUnitContent => ({
    code: src.code,
    quantity: src.quantity,
});

const toTransportOrderHandlingUnit = (src: ApiTransportOrderHandlingUnit): TransportHandlingUnit => ({
    code: src.code,
    quantity: src.quantity,
    totalWeightKg: src.total_weight_kg ?? undefined,
    totalVolumeM3: src.total_volume_m3 ?? undefined,
    content: src.content.map(toTransportOrderHandlingUnitContent),
});

export const toTransportOrder = (src: ApiTransportOrderForShipment): TransportOrder => ({
    transportOrderId: src.transport_order_id,
    transportOrderNumber: src.transport_order_number,
    transportLegNumber: src.transport_leg_number ?? undefined,
    shipFrom: toTransportOrderShippingPartyWithAddress(src.ship_from),
    shipTo: toTransportOrderShippingPartyWithAddress(src.ship_to),
    freightForwarder: toTransportOrderShippingParty(src.freight_forwarder),
    handlingUnits: src.handling_units.map(toTransportOrderHandlingUnit),
    totalWeightKg: src.total_weight_kg ?? undefined,
    totalVolumeM3: src.total_volume_m3 ?? undefined,
    pickupTimeWindow: toTransportOrderTimeWindow(src.pickup_time_window),
    orderedAt: src.ordered_at,
    confirmation: src.confirmation ? toTransportOrderConfirmation(src.confirmation) : undefined,
});

const toTransportOrderConfirmation = (src: ApiTransportOrderConfirmation): TransportOrderConfirmation => ({
    firstConfirmedAt: moment(src.first_confirmed_at).format(),
    confirmedAt: moment(src.confirmed_at).format(),
    freightForwarder: toTransportOrderShippingParty(src.freight_forwarder),
    transportChainReference: src.transport_chain_reference,
    confirmedPickupTimeWindow: src.confirmed_pickup_time_window
        ? toTransportOrderTimeWindow(src.confirmed_pickup_time_window)
        : undefined,
    licensePlate: src.license_plate ?? undefined,
});

export const toTransportOrdersForShipment = (src: ApiTransportOrdersForShipmentsResponse): TransportOrder[] => {
    return src.transport_orders.map(toTransportOrder);
};
